import { HTMLAttributes, useState } from "react";
import { AprovedInstallflowFrame } from "./aproved-install-flow-frame";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "../ui/input";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { useMutation } from "@tanstack/react-query";
import { sendResetCredentialsRequest } from "@/api/api";
import { Loader2, CheckCircle2 } from "lucide-react";

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

const emailSchema = z.object({
  email: z.string().nonempty({ message: "You must input an email address!" }),
});

export function ForgotPassword({ className }: AuthProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const form = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
    mode: "onChange",
  });

  const resetRequestMutation = useMutation({
    mutationFn: async (values: z.infer<typeof emailSchema>) => {
      const res = await sendResetCredentialsRequest(values);
      if (res?.error) {
        form.setError("root", { type: "server", message: res.error });
      } else {
        setSubmittedEmail(values.email);
        setSubmitted(true);
      }
    },
  });

  function onSubmit(values: z.infer<typeof emailSchema>) {
    resetRequestMutation.mutate(values);
  }

  // ── Step 2: success screen ──────────────────────────────────────────────────
  if (submitted) {
    return (
      <AprovedInstallflowFrame>
        <div className="flex flex-col gap-8 w-[350px] animate-in fade-in duration-500">
          <div className="flex items-start">
            <div className="text-blue-600 bg-blue-50 rounded-full p-1">
              <CheckCircle2 size={48} strokeWidth={2.5} />
            </div>
          </div>
          <div className="space-y-3">
            <h2 className="text-xl font-bold text-black leading-tight">
              Check your email
            </h2>
            <p className="text-base font-semibold text-gray-600">
              We sent a password reset link to{" "}
              <span className="text-blue-600">{submittedEmail}</span>.
            </p>
            <p className="text-sm text-gray-500">
              Not seeing it? Make sure to check your spam or junk folder.
            </p>
          </div>
        </div>

        <div className="w-full">
          <Button
            className="bg-[#EDF0F3] text-black w-full h-[50px] px-6 py-2 rounded-[25px] text-base font-semibold"
            onClick={() => {
              setSubmitted(false);
              form.reset();
            }}
          >
            Try a different email
          </Button>
        </div>
      </AprovedInstallflowFrame>
    );
  }

  // ── Step 1: email input form ────────────────────────────────────────────────
  return (
    <AprovedInstallflowFrame>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6 w-full"
        >
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-bold text-black">
              Reset your password
            </h2>
            <p className="text-base font-semibold text-gray-600">
              Enter your work email and we'll send you a link to reset your
              password.
            </p>
          </div>

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1">
                <FormControl>
                  <Input
                    placeholder="Email"
                    className="h-12"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.formState.errors.root?.message && (
            <p className={cn("text-sm font-medium text-destructive", className)}>
              {form.formState.errors.root.message}
            </p>
          )}

          <Button
            variant="installFlow"
            type="submit"
            disabled={!form.formState.isValid || resetRequestMutation.isPending}
            className="h-[54px] w-full"
          >
            {resetRequestMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Send reset link"
            )}
          </Button>
        </form>
      </Form>
    </AprovedInstallflowFrame>
  );
}
